/* eslint-disable no-param-reassign */
import type { Draft } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { castDraft } from "immer";
import { backOff } from "exponential-backoff";
import { findOnlyOrThrow } from "@dhau/lang-extras";
import type { PricingModel } from "@brickme/project-core/src/model/pricing.ts";
import type { Palette } from "@brickme/project-core/src/model/picture.ts";
import { transportBrickColourToCore } from "@brickme/project-core/src/model/bricks.ts";
import type { Country } from "~/frontend-common/localisation.ts";
import type { GetPicToBrickCategoriesQuery } from "~/api/get-pic-to-brick-categories.ts";
import getPicToBrickCategories from "~/api/get-pic-to-brick-categories.ts";
import type { GetSystemPaletteQuery } from "~/api/get-system-palette.ts";
import getSystemPaletteQuery from "~/api/get-system-palette.ts";
import type { GetPricingModelQuery } from "~/api/get-pricing-model.ts";
import getPricingModelQuery from "~/api/get-pricing-model.ts";
import type { GetCountriesQuery } from "~/shopify/get-countries-query.ts";
import { getCountriesQuery } from "~/shopify/get-countries-query.ts";
import type { GetAllCollectionsQuery } from "~/shopify/get-all-collections-query.ts";
import qetAllCollectionsQuery from "~/shopify/get-all-collections-query.ts";
import type {
	GetVariantByIdQuery,
	GetVariantByIdVariables,
} from "~/shopify/get-variant-by-id-query.ts";
import getVariantByIdQuery from "~/shopify/get-variant-by-id-query.ts";
import type { StoreThunkApiConfig } from "~/store-config.ts";
import type {
	PictureFrame,
	HangingStrips,
	PreassemblyProduct,
} from "~/model.ts";
import type { OnceOffLoad } from "~/utils/loading.ts";
import {
	errorOnceOffLoad,
	loadedOnceOffLoad,
	loadingOnceOffLoad,
	isOnceOffLoaded,
	pendingOnceOffLoad,
	performOnceOffLoad,
} from "~/utils/loading.ts";
import type { ShopifyPrices } from "./shopify-prices.ts";
import {
	loadPicPrices,
	loadHangingStrips,
	loadPreassemblyPrices,
	loadFrames,
} from "./load-prices.ts";
import waitForCountries from "./wait-for-countries.ts";
import type { State } from "./state.ts";
import { initialState } from "./state.ts";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";

// Type for gift variant details returned by loadGiftVariantDetails
export type GiftVariantDetails = {
	readonly sessionId: string;
	readonly variantId: string;
	readonly giftSize: GenericSize<number>;
	readonly giftBaseplateSize: number; // Baseplate size (24 or 32)
	readonly giftProductTitle: string;
	readonly giftFramingVariantId?: string;
	readonly giftFramingVariantQty?: number;
	readonly giftFramingType?: "nano-strips" | "frame";
};

const name = "reference";

const ensurePicToBrickCategoriesLoaded = createAsyncThunk<
	GetPicToBrickCategoriesQuery["picToBrickCategories"],
	undefined,
	StoreThunkApiConfig<{ reference: State }>
>(
	`${name}/ensurePicToBrickCategoriesLoaded`,
	async (_, { extra: { apiClient }, getState }) => {
		const {
			reference: { picToBrickCategories },
		} = getState();
		if (picToBrickCategories) {
			return picToBrickCategories;
		}
		const result = await apiClient.request<GetPicToBrickCategoriesQuery>(
			getPicToBrickCategories,
		);
		return result.picToBrickCategories;
	},
);

const ensureShopifyCollectionsLoaded = createAsyncThunk<
	State["shopifyCollections"],
	undefined,
	StoreThunkApiConfig<{ reference: State }>
>(
	`${name}/ensureShopifyCollectionsLoaded`,
	async (_, { extra: { shopifyClient }, getState }) => {
		const {
			reference: { shopifyCollections },
		} = getState();
		// Might result in double load. Can't check against pending because pending
		// action reducer just set to loading
		if (shopifyCollections.type === "loaded") {
			return shopifyCollections;
		}
		return performOnceOffLoad(async () => {
			const result = await shopifyClient.request<GetAllCollectionsQuery>(
				qetAllCollectionsQuery,
			);
			return result.collections.edges.map((edge) => edge.node);
		});
	},
);

const loadSystemPalette = createAsyncThunk<
	OnceOffLoad<Palette>,
	undefined,
	StoreThunkApiConfig
>(`${name}/loadSystemPalette`, async (_, { extra: { apiClient } }) =>
	performOnceOffLoad(async () => {
		const result = await apiClient.request<GetSystemPaletteQuery>(
			getSystemPaletteQuery,
		);
		return result.brickPalette.map(transportBrickColourToCore).map((brick) => ({
			brick,
			limit: undefined,
		}));
	}),
);

const loadPricingModel = createAsyncThunk<
	OnceOffLoad<
		Pick<PricingModel, "numColoursPremiumThreshold" | "numColoursPremium">
	>,
	undefined,
	StoreThunkApiConfig
>(`${name}/loadPricingModel`, async (_, { extra: { apiClient } }) =>
	performOnceOffLoad(async () => {
		const {
			pricingModel: { numColoursPremium, numColoursPremiumThreshold },
		} = await apiClient.request<GetPricingModelQuery>(getPricingModelQuery);
		return {
			numColoursPremium,
			numColoursPremiumThreshold,
		};
	}),
);

const ensurePicPricesLoaded = createAsyncThunk<
	| {
			shopifyPrices: ShopifyPrices;
			hangingStrips?: HangingStrips;
			frames: readonly PictureFrame[];
			preassemblyProduct: PreassemblyProduct | undefined;
	  }
	| undefined,
	string,
	StoreThunkApiConfig<{ reference: State }>
>(
	`${name}/ensurePicPricesLoaded`,
	async (countryCode, { getState, extra: { shopifyClient, apiClient } }) => {
		const {
			reference: { picPricesByCountryCode, basePlateSizes },
		} = getState();
		if (countryCode in picPricesByCountryCode) {
			return undefined;
		}

		const countries = await waitForCountries(getState);

		// A bit hacky, but works for our purposes because only worried about prices
		const { currencyCode } = findOnlyOrThrow(
			countries,
			(c) => c.code === countryCode,
			`Country not found when ensuring pic prices ${countryCode}`,
		);

		const [picPrices, hangingStripsCombo, preassemblyCombo, framesCombo] =
			await Promise.all([
				loadPicPrices(apiClient, countryCode, currencyCode),
				loadHangingStrips(shopifyClient, countryCode),
				loadPreassemblyPrices(shopifyClient, countryCode),
				loadFrames(shopifyClient, countryCode, basePlateSizes),
			]);
		const preassemblyPricesByVariantId =
			preassemblyCombo.product && preassemblyCombo.price
				? {
						[preassemblyCombo.product.productVariantId]: preassemblyCombo.price,
					}
				: {};
		return {
			shopifyPrices: {
				pic: picPrices,
				hangingStripsPrice: hangingStripsCombo?.price,
				preassemblyPricesByVariantId,
				framePricesByVariantId: framesCombo.prices,
			},
			preassemblyProduct: preassemblyCombo.product,
			frames: framesCombo.frames,
			hangingStrips: hangingStripsCombo?.hangingStrips,
		};
	},
);

const loadGiftVariantDetails = createAsyncThunk<
	GiftVariantDetails,
	{
		readonly sessionId: string;
		readonly variantId: string;
		readonly framingVariantId?: string;
		readonly framingVariantQty?: number;
		readonly countryCode: string;
	},
	StoreThunkApiConfig<{ reference: State }>
>(
	`${name}/loadGiftVariantDetails`,
	// eslint-disable-next-line @typescript-eslint/unbound-method
	async (
		{ sessionId, variantId, framingVariantId, framingVariantQty, countryCode },
		{ extra: { shopifyClient }, getState, dispatch },
	) => {
		// Convert numeric variant ID to Shopify global ID format
		const shopifyGlobalId = `gid://shopify/ProductVariant/${variantId}`;

		const data = await shopifyClient.request<
			GetVariantByIdQuery,
			GetVariantByIdVariables
		>(getVariantByIdQuery, { id: shopifyGlobalId, countryCode });

		const node = data.node;
		if (!node) {
			throw new Error(`Node not found: ${variantId}`);
		}

		if (node.__typename !== "ProductVariant") {
			throw new Error(`Node is not a ProductVariant: ${node.__typename}`);
		}

		const variant = node;

		// Extract size from product title (e.g., "Custom Design Brick Mosaic - 9x9 baseplates")
		//TODO: Use Metafields instead. Translations are affected by this.
		const titleSizeRegex = /(\d+)x(\d+)/;
		const match = variant.product.title.match(titleSizeRegex);
		if (!match) {
			throw new Error(
				`Invalid product title format for variant: ${variantId}, title: ${variant.product.title}`,
			);
		}

		const [, dimension1, dimension2] = match;
		const giftSize: GenericSize<number> = {
			width: parseInt(dimension1, 10),
			height: parseInt(dimension2, 10),
		};

		// Determine baseplate size from variant options
		// These are system generated values so should be safe to parse. May want to look at using Metafields instead.
		const baseplateSizeOption = variant.selectedOptions.find(
			(option) => option.name.toLowerCase() === "baseplate size",
		);
		if (!baseplateSizeOption) {
			throw new Error(
				`Could not find baseplate size option in variant: ${variantId}`,
			);
		}

		// Extract the numeric size from the option value (e.g., "24x24 studs" -> 24)
		const baseplateSizeMatch = baseplateSizeOption.value.match(/(\d+)x\d+/);
		if (!baseplateSizeMatch) {
			throw new Error(
				`Invalid baseplate size format in option value: ${baseplateSizeOption.value}`,
			);
		}

		const giftBaseplateSize = parseInt(baseplateSizeMatch[1], 10);

		// Validate that the baseplate size is one of the supported sizes
		const { basePlateSizes } = getState().reference;
		if (!basePlateSizes.includes(giftBaseplateSize)) {
			throw new Error(
				`Unsupported baseplate size: ${giftBaseplateSize}. Supported sizes: ${basePlateSizes.join(", ")}`,
			);
		}

		// Determine framing type and validate quantity
		let giftFramingType: "nano-strips" | "frame" | undefined;

		if (framingVariantId) {
			// Ensure pricing data is loaded to check variant types
			await dispatch(ensurePicPricesLoaded(countryCode));
			const { reference } = getState();

			// Check if it's a nano strips variant
			if (isOnceOffLoaded(reference.hangingStrips)) {
				const nanoStripsVariantId =
					reference.hangingStrips.data?.productVariantId;
				if (nanoStripsVariantId === framingVariantId) {
					giftFramingType = "nano-strips";
				}
			}

			// Check if it's a frame variant
			if (!giftFramingType && isOnceOffLoaded(reference.frames)) {
				const frameVariant = reference.frames.data
					.flatMap((frame) => frame.variants)
					.find((variant) => variant.productVariantId === framingVariantId);

				if (frameVariant) {
					giftFramingType = "frame";
				}
			}

			// If we couldn't determine the type, throw an error
			if (!giftFramingType) {
				throw new Error(
					`Could not determine framing type for variant ID: ${framingVariantId}`,
				);
			}

			// Validate quantity based on framing type
			if (giftFramingType === "nano-strips") {
				if (!framingVariantQty || isNaN(framingVariantQty)) {
					throw new Error(
						`Nano strips require a valid quantity. Received: ${framingVariantQty}`,
					);
				}
			} else if (giftFramingType === "frame") {
				// For frames, default to quantity 1 if not provided
				if (!framingVariantQty || isNaN(framingVariantQty)) {
					framingVariantQty = 1;
				}
			}
		}

		return {
			sessionId,
			variantId,
			giftSize,
			giftBaseplateSize,
			giftProductTitle: variant.product.title,
			giftFramingVariantId: framingVariantId,
			giftFramingVariantQty: framingVariantQty,
			giftFramingType: giftFramingType,
		} satisfies GiftVariantDetails;
	},
);

const loadCountries = createAsyncThunk<
	readonly Country[],
	undefined,
	StoreThunkApiConfig
>(`${name}/loadCountries`, async (_, { extra: { shopifyClient } }) =>
	backOff(
		async () => {
			const data =
				await shopifyClient.request<GetCountriesQuery>(getCountriesQuery);
			if (!data) {
				throw new Error("Error loading countries - no data");
			}
			return data.localization.availableCountries.map((a) => ({
				code: a.isoCode,
				name: a.name,
				currencyCode: a.currency.isoCode,
				currencySymbol: a.currency.symbol,
			}));
		},
		{
			timeMultiple: 1.5,
		},
	),
);

const slice = createSlice({
	name,
	initialState,
	reducers: {},
	extraReducers(builder) {
		builder.addCase(loadCountries.pending.type, (state: Draft<State>) => {
			state.countries = pendingOnceOffLoad;
		});
		builder.addCase(
			loadCountries.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof loadCountries)["fulfilled"]>>,
			) => {
				state.countries = loadedOnceOffLoad(action.payload);
			},
		);
		builder.addCase(loadCountries.rejected.type, (state: Draft<State>) => {
			state.countries = errorOnceOffLoad("Error loading countries");
		});
		builder.addCase(
			loadSystemPalette.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof loadSystemPalette)["fulfilled"]>>,
			) => {
				state.systemPalette = action.payload;
			},
		);
		builder.addCase(
			ensureShopifyCollectionsLoaded.pending.type,
			(state: Draft<State>) => {
				if (state.shopifyCollections.type === "pending") {
					state.shopifyCollections = loadingOnceOffLoad;
				}
			},
		);
		builder.addCase(
			ensureShopifyCollectionsLoaded.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<
					ReturnType<(typeof ensureShopifyCollectionsLoaded)["fulfilled"]>
				>,
			) => {
				state.shopifyCollections = action.payload;
			},
		);
		builder.addCase(
			loadPricingModel.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof loadPricingModel)["fulfilled"]>>,
			) => {
				state.pricingModel = action.payload;
			},
		);
		builder.addCase(
			ensurePicToBrickCategoriesLoaded.fulfilled.type,
			(
				state: Draft<State>,
				action: ReturnType<
					(typeof ensurePicToBrickCategoriesLoaded)["fulfilled"]
				>,
			) => {
				state.picToBrickCategories = castDraft(action.payload);
			},
		);
		builder.addCase(
			ensurePicPricesLoaded.pending.type,
			(state: Draft<State>) => {
				if (!isOnceOffLoaded(state.hangingStrips)) {
					state.hangingStrips = loadingOnceOffLoad;
				}
				if (!isOnceOffLoaded(state.frames)) {
					state.frames = loadingOnceOffLoad;
				}
				if (!isOnceOffLoaded(state.preassemblyProduct)) {
					state.preassemblyProduct = loadingOnceOffLoad;
				}
			},
		);
		builder.addCase(
			ensurePicPricesLoaded.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof ensurePicPricesLoaded)["fulfilled"]>>,
			) => {
				if (!action.payload) {
					return;
				}

				const { shopifyPrices, hangingStrips, frames, preassemblyProduct } =
					action.payload;
				state.picPricesByCountryCode[action.meta.arg] = shopifyPrices;
				state.hangingStrips = loadedOnceOffLoad(hangingStrips);
				state.frames = loadedOnceOffLoad(frames);
				state.preassemblyProduct = loadedOnceOffLoad(preassemblyProduct);
			},
		);
		builder.addCase(
			ensurePicPricesLoaded.rejected.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof ensurePicPricesLoaded)["rejected"]>>,
			) => {
				state.hangingStrips = errorOnceOffLoad(
					action.error.message ?? "Unknown error",
				);
				state.frames = errorOnceOffLoad(
					action.error.message ?? "Unknown error",
				);
				state.preassemblyProduct = errorOnceOffLoad(
					action.error.message ?? "Unknown error",
				);
			},
		);
	},
});

export {
	ensurePicToBrickCategoriesLoaded,
	loadSystemPalette,
	loadPricingModel,
	ensurePicPricesLoaded,
	loadCountries,
	ensureShopifyCollectionsLoaded,
	loadGiftVariantDetails,
};
export default slice.reducer;
